import React, { useContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import "../../Dashboard/View_List/View_List.css";
import "./ManualResult.css";

import {
  findAll,
  getManualResult,
  setManualResult,
} from "../../../utils/apis/win3x/manual_result/manual_result";
import { Context } from "../../../utils/context";
import Heading_Holder from "../../Common_Component/Heading_Holder/Heading_Holder";

const SIDE_NAMES = ["A", "B", "C"];
const MULTIPLES = [1, 2, 3, 4, 5, 10];
const SOCKET_URL =
  process.env.REACT_APP_SWAP_SOCKET_URL || "http://127.0.0.1:4243";

const PHASE_LABELS = {
  display: "Starting",
  shuffling: "Shuffling",
  betting: "Betting open",
  closed: "Betting closed",
  result: "Result",
  win_popup: "Result",
};

const formatTime = (ms) =>
  ms
    ? new Date(ms).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
    : "-";

const Tables = () => {
  const { UnauthorizedLogOut } = useContext(Context);

  const [state, setState] = useState(null); // latest q88_state from win3xswapgame
  const [sides, setSides] = useState([]);
  const lastFetch = useRef({ roundId: null, at: 0 });

  // Live q88_state from the swap game server (every second).
  useEffect(() => {
    const socket = io(SOCKET_URL, { query: { type: "admin" } });
    socket.on("q88_state", setState);
    socket.on("q88_new_round", setState);
    return () => socket.disconnect();
  }, []);

  // Bet counts/amounts for the current round: on round change, then every 2s.
  const roundId = state?.round_id;
  const tick = state?.server_time;
  useEffect(() => {
    if (!roundId) return;
    const now = Date.now();
    const last = lastFetch.current;
    if (last.roundId === roundId && now - last.at < 2000) return;
    lastFetch.current = { roundId, at: now };
    findAll(roundId)
      .then((res) => setSides(res?.data?.data?.sides || []))
      .catch((error) => UnauthorizedLogOut(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId, tick]);

  // Arrange cards as per the shuffle: apply the swaps that have run so far to
  // initial_cards; once the result is declared use final_cards as sent.
  let cards = state?.result?.final_cards;
  if (!cards) {
    cards = [...(state?.initial_cards || [])];
    const { swaps = [], swap_ms = 1000 } = state?.shuffle || {};
    const done = Math.max(
      0,
      Math.min(
        swaps.length,
        Math.floor((state?.server_time - state?.shuffle_start_at) / swap_ms),
      ),
    );
    for (let i = 0; i < done; i++) {
      const [a, b] = swaps[i];
      [cards[a], cards[b]] = [cards[b], cards[a]];
    }
  }
  // Manual pick (0/1/2 or null = automatic) for the current round.
  const [manualPick, setManualPick] = useState(null);
  const [multiple, setMultiple] = useState(1);
  const [pickError, setPickError] = useState("");
  useEffect(() => {
    setPickError("");
    if (!roundId) return;
    getManualResult(roundId)
      .then((res) => {
        setManualPick(res?.data?.data?.position ?? null);
        setMultiple(res?.data?.data?.multiple ?? 1);
      })
      .catch(() => {
        setManualPick(null);
        setMultiple(1);
      });
  }, [roundId]);

  // Can be changed until the result is declared.
  const canPick =
    !!state &&
    !state.result &&
    state.server_time < state.betting_start_at + 50 * 1000;

  const pick = async (position, nextMultiple = multiple) => {
    setPickError("");
    try {
      await setManualResult(roundId, position, nextMultiple);
      setManualPick(position);
      setMultiple(position === null ? 1 : nextMultiple);
    } catch (error) {
      setPickError(
        error?.response?.data?.message ||
          error?.response?.data?.data ||
          error.message,
      );
    }
  };

  const winning = state?.result?.winning_position;
  const phase = state?.phase;
  const totalAmount = sides.reduce((a, s) => a + s.total_amount, 0);
  const totalCount = sides.reduce((a, s) => a + s.bet_count, 0);

  return (
    <section className="View_List mr-wrap">
      <Heading_Holder headingText={"Manual Result"} />

      <div className="mr-info">
        <div className="mr-info-item">
          <span>Period</span>
          <b>{state?.period ?? "-"}</b>
        </div>
        <div className="mr-info-item">
          <span>Status</span>
          <b>{PHASE_LABELS[phase] || phase || "Connecting..."}</b>
        </div>
        <div className="mr-info-item mr-timer">
          <span>Time left</span>
          <b>{state?.time_left ?? "-"}s</b>
        </div>
        <div className="mr-info-item">
          <span>Round started</span>
          <b>{formatTime(state?.betting_start_at)}</b>
        </div>
        <div className="mr-info-item">
          <span>Betting closes</span>
          <b>{formatTime(state?.betting_close_at)}</b>
        </div>
        <div className="mr-info-item">
          <span>Server time</span>
          <b>{formatTime(state?.server_time)}</b>
        </div>
      </div>

      {canPick && (
        <div className="mr-multiple">
          <label htmlFor="mr-multiple-select">Winning multiple</label>
          <select
            id="mr-multiple-select"
            value={multiple}
            onChange={(e) => {
              const next = Number(e.target.value);
              // Already picked a side: save the new multiple right away.
              if (manualPick !== null) pick(manualPick, next);
              else setMultiple(next);
            }}>
            {MULTIPLES.map((m) => (
              <option key={m} value={m}>
                {m}X
              </option>
            ))}
          </select>
          {/* <div className="mr-multiple-hint">
            <span className="mr-multiple-hint-icon">i</span>
            <span>
              Applied on top of the App Setup payout
              <span className="mr-multiple-example">30 × 3 = 90</span>
            </span>
          </div> */}
        </div>
      )}

      <div className="mr-cards">
        {SIDE_NAMES.map((name, position) => {
          const side = sides.find((s) => s.position === position);
          const isWinner = winning === position;
          const isPicked = manualPick === position;
          const revealed = !!state?.result;
          const rawFace = revealed ? cards[position] : isPicked ? "A" : null;
          // Server sends Q / 8: show Q as A and the 8 as a blank card.
          const rank = String(rawFace ?? "").toUpperCase();
          const face = rank === "Q" ? "A" : rank === "8" ? "" : rawFace;
          const isFace = face !== null && face !== undefined;
          return (
            <div
              key={name}
              className={`mr-card ${isWinner ? "winner" : ""} ${isPicked && !revealed ? "picked" : ""}`}>
              {isWinner && <span className="mr-badge">WINNER</span>}
              <div className="mr-side">{name}</div>
              <div
                className={`mr-playing-card ${isFace ? "face" : "back"} ${face === "A" ? "queen" : ""} ${isPicked && !revealed ? "chosen" : ""}`}>
                {face}
              </div>
              <div className="mr-amount-label">Points placed on {name}</div>
              <div className="mr-amount">
                &#8377; {Number(side?.total_amount ?? 0).toFixed(2)}
              </div>
              <div className="mr-bets">{side?.bet_count ?? 0} bets</div>
              {canPick && (
                <button
                  type="button"
                  className={`mr-btn ${isPicked ? "active" : ""}`}
                  onClick={() => pick(isPicked ? null : position)}>
                  {isPicked
                    ? "A will open here (click to undo)"
                    : "Open A here"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className={`mr-status ${manualPick === null ? "" : "manual"}`}>
        {manualPick === null
          ? "Result: automatic"
          : `A will open on${SIDE_NAMES[manualPick]} (set manually, ${multiple}X winning)`}
      </div>
      {pickError && <div className="mr-error">{pickError}</div>}

      <div className="mr-total">
        Total: <b>{totalCount}</b> bets, &#8377; <b>{totalAmount.toFixed(2)}</b>
      </div>
    </section>
  );
};

export default Tables;
